import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import CompassLogo from "./CompassLogo";
import { useDocument } from "@/hooks/useDocument";
import { useDictation } from "@/hooks/useDictation";
import {
  useCompassChatHistory,
  useInvalidateCompassChat,
  transformCompassHistory,
} from "@/hooks/useCompassChat";
import { compasAIQueryStream } from "@/services/compass.service";
import { processCompassStream } from "./CompassDialog.utils";
import { CompassMessagesArea, CompassInputArea } from "./CompassDialogSubComponents";
import type { ICompassMessage } from "@/types/compass.types";

interface CompassDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const useCompassMessages = (
  historyMessages: ICompassMessage[],
  optimisticMessages: ICompassMessage[]
) => {
  return React.useMemo(
    () => [...historyMessages, ...optimisticMessages],
    [historyMessages, optimisticMessages]
  );
};

const useAutoScrollToBottom = (
  messagesLength: number,
  isAssistantTyping: boolean,
  messagesEndRef: React.RefObject<HTMLDivElement | null>
) => {
  const prevMessagesLengthRef = React.useRef(0);
  React.useEffect(() => {
    if (messagesLength > prevMessagesLengthRef.current || isAssistantTyping)
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    prevMessagesLengthRef.current = messagesLength;
  }, [messagesLength, isAssistantTyping]);
};

const buildUserMessage = (userMessage: string): ICompassMessage => ({
  isSender: true,
  canCopy: false,
  text: userMessage,
  creation_date: new Date().toISOString(),
});

const resetTextareaHeight = (textareaRef: React.RefObject<HTMLTextAreaElement | null>) => {
  if (textareaRef.current) textareaRef.current.style.height = "20px";
};

const useSendCompassMessage = (
  chatMessage: string,
  doc: ReturnType<typeof useDocument>["data"],
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>,
  setOptimisticMessages: React.Dispatch<React.SetStateAction<ICompassMessage[]>>,
  invalidateCompassChat: ReturnType<typeof useInvalidateCompassChat>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) => {
  const handleSendMessage = async () => {
    if (!chatMessage.trim() || doc?._id == null || !doc?.title) return;
    const userMessage = chatMessage.trim();
    setChatMessage("");
    resetTextareaHeight(textareaRef);
    setOptimisticMessages((prev) => [...prev, buildUserMessage(userMessage)]);
    setIsAssistantTyping(true);
    try {
      const stream = await compasAIQueryStream({
        text: userMessage,
        document_ids: [doc._id],
        current_document_id: doc._id,
        document_title: [doc.title],
      });
      await processCompassStream(stream);
      setIsAssistantTyping(false);
      if (doc?._id) await invalidateCompassChat(doc._id);
      setOptimisticMessages([]);
    } catch (error) {
      console.error("Failed to send message:", error);
      setIsAssistantTyping(false);
      setOptimisticMessages([]);
    }
  };
  return handleSendMessage;
};

const buildTextareaResizeHandler = (
  setChatMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  return (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatMessage(event.target.value);
    const ta = event.target;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  };
};

const CompassDialog: React.FC<CompassDialogProps> = ({ isOpen, onClose }) => {
  const [chatMessage, setChatMessage] = React.useState("");
  const [isAssistantTyping, setIsAssistantTyping] = React.useState(false);
  const [optimisticMessages, setOptimisticMessages] = React.useState<ICompassMessage[]>([]);
  const { data: doc } = useDocument();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { data: chatHistory, isLoading } = useCompassChatHistory(doc?._id ?? "");
  const historyMessages = transformCompassHistory(chatHistory);
  const invalidateCompassChat = useInvalidateCompassChat();
  const { isRecording, isMicroLoading, isConverting, startDictation, isSpeaking } = useDictation({
    onTranscript: setChatMessage,
  });

  React.useEffect(() => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const sc = scrollContainerRef.current;
    const near = sc ? sc.scrollHeight - sc.scrollTop <= sc.clientHeight + 50 : false;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    if (near && sc) sc.scrollTop = sc.scrollHeight;
  }, [chatMessage, isSpeaking]);

  const messages = useCompassMessages(historyMessages, optimisticMessages);
  useAutoScrollToBottom(messages.length, isAssistantTyping, messagesEndRef);

  const handleSendMessage = useSendCompassMessage(
    chatMessage,
    doc,
    setChatMessage,
    setIsAssistantTyping,
    setOptimisticMessages,
    invalidateCompassChat,
    textareaRef
  );

  const handleTextareaChange = buildTextareaResizeHandler(setChatMessage);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-11/12 max-h-[90vh] h-full flex flex-col p-0">
        <DialogHeader className="flex-row items-center justify-between px-4 py-2 border-b">
          <div className="flex items-center gap-3">
            <CompassLogo containerClassName="size-10" />
            <DialogTitle className="text-base font-medium">AI Compass</DialogTitle>
          </div>
        </DialogHeader>
        <div className="flex-1 flex flex-col min-h-0">
          <CompassMessagesArea
            messages={messages}
            isLoading={isLoading}
            isAssistantTyping={isAssistantTyping}
            scrollContainerRef={scrollContainerRef}
            messagesEndRef={messagesEndRef}
          />
          <CompassInputArea
            isRecording={isRecording}
            isMicroLoading={isMicroLoading}
            isSpeaking={isSpeaking}
            isConverting={isConverting}
            chatMessage={chatMessage}
            isAssistantTyping={isAssistantTyping}
            textareaRef={textareaRef}
            handleTextareaChange={handleTextareaChange}
            handleSendMessage={handleSendMessage}
            startDictation={startDictation}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompassDialog;
